import { createSelector } from "reselect";
import get from "lodash/get";
import map from "lodash/map";
import find from "lodash/find";
import head from "lodash/head";
import filter from "lodash/filter";
import { urlParam } from "utils/helpers";
import {
  formatDisplayNumber,
  formatCurrency,
  getNumberType
} from "utils/helpers";
import {
  numberFormatSelector,
  currencyFormatSelector,
  genericSelector
} from "./appSettings";
import { selectedNumberTypeSelector } from "./order";
import {
  SIM_TYPES,
  NUMBER_TYPES,
  CONTRACT_TYPES,
  NUMBER_CATEGORIES
} from "utils/constants";
import { inCompleteOrderSelector } from "ducks/selectors";
import { planTypeSelector } from "./planService";
import { currentOrderSelector } from "./guestOrder";

const numbersDomain = state => get(state, "api.numbers.data", {});

const numbersPriceDomain = state =>
  get(state, "api.numbers.data.selectedNumber.price", {});

export const selectedNumberPriceDomain = state => {
  return get(state, "api.selectedNumberPriceDetails.data", null);
};

export const selectedNumberPriceSelector = createSelector(
  selectedNumberPriceDomain,
  state => {
    return state;
  }
);

const numbersCategoriesDomain = state =>
  get(state, "api.NumberCategories.data.availableCategories", []);

export const preCheckoutSelector = state =>
  get(state, "form.PreCheckoutForm.values", {});

export const numberTypeSelector = createSelector(
  inCompleteOrderSelector,
  order => {
    return urlParam("number_type") === NUMBER_TYPES.PORT_IN ||
      get(order, "number_type") === CONTRACT_TYPES.PORT_IN
      ? CONTRACT_TYPES.PORT_IN
      : CONTRACT_TYPES.NEW;
  }
);

const selectedNumberDomain = state => {
  return get(state, "api.lockedNumber.data");
};

export const lockedNumberSelector = createSelector(
  selectedNumberDomain,
  state => {
    return state;
  }
);

const searchNumberKeyDomain = state => {
  return get(state, "api.setSearchNumberKey.data");
};

const filteredNumbersDomain = state => {
  return get(state, "api.filteredNumbers.data");
};

const filteredNumbersLoading = state => {
  return get(state, "api.filteredNumbers.loading");
};
// ***************************************************************
// ********************** numbers selectors **********************
// ***************************************************************

export const selectedNumberLoader = createSelector(
  filteredNumbersLoading,
  isLoading => isLoading
);

export const selectedSimTypeSelector = createSelector(
  genericSelector,
  inCompleteOrderSelector,
  (generic, order) => {
    return get(order, "sim_type") === SIM_TYPES.E_SIM ||
      urlParam("sim_type") === SIM_TYPES.ESIM
      ? SIM_TYPES.E_SIM
      : get(order, "sim_type") === SIM_TYPES.PHYSICAL_SIM
      ? SIM_TYPES.PHYSICAL_SIM
      : get(generic, "default_sim_type", SIM_TYPES.PHYSICAL_SIM);
  }
);

export const selectedNumberSelector = createSelector(
  selectedNumberDomain,
  numbersDomain,
  currentOrderSelector,
  (selectedNumber, numbers, currentOrder) => {
    let number =
      selectedNumber ||
      get(numbers, "consumedNumbers[0]") ||
      get(numbers, "selectedNumber") ||
      get(currentOrder, "selected_number");

    number = number || { sku: "TEL-NUMGEN" };
    return { ...number, sku: number.sku || "TEL-NUMGEN" };
  }
);

export const selectedSimLockExpireTimeSelector = createSelector(
  selectedNumberSelector,
  numberData =>
    (get(numberData, "lock_expire_at", 0) ||
      get(numberData, "lockExpireAt", 0)) - 1
);

export const availableNumbersSelector = createSelector(
  numbersDomain,
  numbers => {
    return get(numbers, "availableNumbers");
  }
);

export const numberLockErrorSelector = createSelector(
  numbersDomain,
  numbers => {
    return get(numbers, "error");
  }
);

export const selectedNumberCategoryDomain = state => {
  return get(state, "api.selectedNumberCategory.data");
};

export const NumberSettingsSelector = createSelector(
  genericSelector,
  inCompleteOrderSelector,
  planTypeSelector,
  selectedNumberCategoryDomain,
  searchNumberKeyDomain,
  numbersCategoriesDomain,
  (generic, order, planType, numberCategory, searchKey, categoryList) => {
    if (categoryList?.length) {
      numberCategory = get(categoryList[0], "category");
    }
    const numberSettings = get(generic, "fetch_number_settings", {});
    const numberType = head(get(numberSettings, "number_types"));
    let argNumberType;
    if (typeof searchKey === "undefined" || searchKey?.key === "") {
      argNumberType = [{ ...numberType, numberCategory }];
    } else {
      argNumberType = [{ ...numberType, numberCategory, search: searchKey }];
    }
    return {
      numberTypes: argNumberType,
      preselectNumber: true,
      ...(get(order, "state") === "IC" && { preselectNumber: false })
    };
  }
);

export const numberSeriesSelector = createSelector(numbersDomain, numbers => {
  return get(numbers, "series");
});

export const silverNumbersSelector = createSelector(numbersDomain, numbers => {
  const numberList = find(get(numbers, "availableNumbers", []), {
    sku: "TEL-NUMSILVER"
  });
  return get(numberList, "numbers");
});

export const goldenNumbersSelector = createSelector(numbersDomain, numbers => {
  const numberList = find(get(numbers, "availableNumbers", []), {
    sku: "TEL-NUMGOLD"
  });
  return get(numberList, "numbers");
});

export const silverNumbersPriceSelector = createSelector(
  numbersDomain,
  numbers => {
    return get(numbers, "silver_numbers.price");
  }
);

export const goldenNumbersPriceSelector = createSelector(
  numbersDomain,
  numbers => {
    return get(numbers, "golden_numbers.price");
  }
);

// ***************************************************************
// ********************* numbers makeSelect **********************
// ***************************************************************

export const makeSelectAvailableNumbers = () =>
  createSelector(
    numberFormatSelector,
    availableNumbersSelector,
    selectedNumberSelector,
    currencyFormatSelector,
    (format, numbers, selectedNumber, currencyFormat) => {
      return map(numbers, numberData => {
        const number = get(numberData, "number");
        return {
          id: number,
          label: number,
          value: number,
          selected: number === get(selectedNumber, "number"),
          price: {
            value: numberData.price.value,
            currency: formatCurrency(
              numberData.price.value,
              currencyFormat,
              true
            )
          },
          numberCategory: numberData.numberCategory
        };
      });
    }
  );

export const makeSelectSilverNumbers = () =>
  createSelector(
    numberFormatSelector,
    silverNumbersSelector,
    selectedNumberSelector,
    (format, numbers, selectedNumber) => {
      return map(numbers, number => {
        return {
          id: number,
          label: formatDisplayNumber(number, format),
          value: number,
          selected: number === get(selectedNumber, "number")
        };
      });
    }
  );

export const makeSelectGoldenNumbers = () =>
  createSelector(
    numberFormatSelector,
    goldenNumbersSelector,
    selectedNumberSelector,
    (format, numbers, selectedNumber) => {
      return map(numbers, number => {
        return {
          id: number,
          label: formatDisplayNumber(number, format),
          value: number,
          selected: number === get(selectedNumber, "number")
        };
      });
    }
  );

export const makeSelectSelectedNumber = () => selectedNumberSelector;

export const formattedSelectedNumberSelector = createSelector(
  currencyFormatSelector,
  selectedNumberSelector,
  selectedNumberTypeSelector,
  (currencyFormat, selectedNumber, selectedNumberType) => {
    let { number, price, sku } = selectedNumber || {};
    const { isPortIn } = selectedNumberType || {};
    const productCategoryValue =
      selectedNumber?.productCategory || selectedNumber?.product_category;

    if (selectedNumber && selectedNumber.number) {
      price = price?.value;
    }

    const formattedNumber = number;
    const formattedPrice = formatCurrency(price, currencyFormat, true);
    const type = getNumberType({ sku, isPortIn });

    return {
      sku,
      number,
      price,
      type,
      formattedNumber,
      formattedPrice,
      productType: productCategoryValue
    };
  }
);

export const makeSelectFormattedSelectedNumber = () =>
  formattedSelectedNumberSelector;

export const makeSelectSilverNumbersPrice = () =>
  createSelector(
    currencyFormatSelector,
    silverNumbersPriceSelector,
    (currencyFormat, price) => {
      return {
        price,
        formattedPrice: formatCurrency(price, currencyFormat, true)
      };
    }
  );

export const makeSelectGoldenNumbersPrice = () =>
  createSelector(
    currencyFormatSelector,
    goldenNumbersPriceSelector,
    (currencyFormat, price) => {
      return {
        price,
        formattedPrice: formatCurrency(price, currencyFormat, true)
      };
    }
  );

export const fetchPlanCounter = createSelector(
  preCheckoutSelector,
  formValues => {
    return get(formValues, "_fetch_plan_counter", {
      acc: 0,
      max: 3
    });
  }
);

export const makeFilteredNumbersSelector = () =>
  createSelector(filteredNumbersDomain, filterNumbers => {
    return filterNumbers;
  });

export const selectedCategorySelector = () =>
  createSelector(selectedNumberCategoryDomain, category => {
    return category;
  });
export const makeInitialPriceSelector = () =>
  createSelector(numbersPriceDomain, number => {
    return number;
  });

export const numberCategoriesSelector = () =>
  createSelector(numbersCategoriesDomain, state => {
    const categoryList = filter(
      state,
      item => item?.category !== NUMBER_CATEGORIES?.STANDARD
    );

    const categoryListWithLabel = map(categoryList, categoryItem => {
      return {
        category: categoryItem?.category,
        label: `${categoryItem?.category}_label`
      };
    });

    return categoryListWithLabel;
  });

export const numberTypeCategorySelctor = () => {
  return [
    {
      value: "Standard",
      label: "freenumber_label"
    },
    {
      value: "Premium",
      label: "premiumnumber_label"
    }
  ];
};

export const makeSelectFetchPlanCounter = () => fetchPlanCounter;

export const makeSelectNumberSeries = () => numberSeriesSelector;

export const makeSelectNumberLockError = () => numberLockErrorSelector;

export const makeSelectSelectedSimType = () => selectedSimTypeSelector;

export const makeSelectPrecheckoutInfo = () => preCheckoutSelector;

export const makeSelectNumberType = () => numberTypeSelector;

export const makeSelectFetchNumberSettings = () => NumberSettingsSelector;

export const makeSelectedNumberPriceSelector = () =>
  selectedNumberPriceSelector;

export const makeSelectedSimLockExpireTimeSelector = () =>
  selectedSimLockExpireTimeSelector;

export const makeSelectLoadingNumber = () => selectedNumberLoader;

export const makeSelectCategoryTypeListSelector = () =>
  numberTypeCategorySelctor;
